import { type IOption, type ModuleTheme } from "@/interfaces";
import {
  type DatePresets,
  type EditRepeatTypes,
  type Modules,
  type RruleDaysState,
} from "@/types";
import {
  Delete_Type,
  User_Roles,
  type NotificationSettingsInput,
} from "@/graphql/generated-types";
import { FileAcceptTypes, FileSizes } from "@/enums";
import { globali18n } from "@/i18n";

const { t } = globali18n;

export const TIME_SPLIT_INTERVAL = 15;

export const BREAKPOINTS = {
  mobile: 0,
  tablet: 620,
  laptop: 940,
  desktop: 1150,
  "big-desktop": 1400,
};

export const PRODUCTION_HOSTNAMES: string[] = [
  "ibgmanager.dk",
  "old.ibgmanager.dk",
  "ibg.proreact.dk",
];

export const OLD_MANAGER_URLS: string[] = [
  "https://old.ibgmanager.test",
  "https://ibgmanager.test",
  "https://old.staging.ibgmanager.dk",
  "https://old.ibgmanager.dk",
];

export const CORE_MANAGER_URLS: string[] = [
  "https://ibg.test",
  "https://api.staging.ibgmanager.dk",
  "https://ibg.proreact.dk",
];

export const MOBILE_NAV_HEIGHT = "4.5rem";

export const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL;
export const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM;
export const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;
export const KEYCLOAK_REDIRECT_URI = `${window.location.origin}/callback`;
export const APP_API_URL =
  import.meta.env.VITE_APP_API_URL || "https://api.ibgmanager.dk/ibg";
export const USE_LOCAL_IFRAME = !!import.meta.env.VITE_APP_USE_LOCAL_IFRAME;
export const MANAGER_TYPE = import.meta.env.VITE_MANAGER_TYPE || "IBG_MANAGER";
export const IS_IBG_MANAGER = MANAGER_TYPE === "IBG_MANAGER";
export const IS_BP_MANAGER = MANAGER_TYPE === "BP_MANAGER";
export const PIXABAY_API_KEY = "3154217-f968d4ae329eb903e0f44a575";
export const MAX_PIXABAY_PAGES = 5;
export const MAX_MEAL_DISH_COUNT = 4;

// Define all possible participant keys in one place
export const PARTICIPANT_KEYS = {
  SIGNUP: ["employeesSignedUp", "residentsSignedUp"] as const,
  RESPONSIBLE: ["employeesResponsible", "residentsResponsible"] as const,
} as const;

export const MAX_AGENDA_POINT_LENGTH = 250;

export const YOUTUBE_REGEX =
  /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)[\w-]{11}([?&].*)?$/;

export const VIMEO_REGEX =
  /^(https?:\/\/)?((www|player)\.)?vimeo\.com\/(video\/)?\d+/;

export const DREAMBROKER_REGEX =
  /^https?:\/\/dreambroker\.com\/channel\/[a-z0-9]+\/iframe\/[a-z0-9]+#?$/i;

export const VIDEO_PREVIEW_WIDTH = 316;
export const VIDEO_PREVIEW_HEIGHT = 185;

export const ALLOWED_USER_ROLES: User_Roles[] = [
  User_Roles.Employee,
  User_Roles.Support,
  User_Roles.Superuser,
  User_Roles.Admin,
];

export const PLANNER_ROUTES = [
  "planner/edit-task",
  "planner/day-view",
  "planner/day-view/specific",
  "planner/search",
  "planner/overdue/specific",
  "planner/week-view/specific",
];

export const ALLOWED_IFRAME_ORIGINS = [
  ...CORE_MANAGER_URLS,
  ...OLD_MANAGER_URLS,
];

export const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettingsInput = {
  status: false,
  triggerWhenCancelled: true,
  triggerWhenTimeChange: true,
  reminderFirst: 1440,
  reminderSecond: 15,
};

export const NOTIFICATION_OPTIONS: IOption[] = [
  {
    value: null,
    title: t("global.none"),
  },
  {
    value: 0,
    title: t("global.notificationIntervals.on_start_time"),
  },
  {
    value: 5, // 5 minutw
    title: t("global.notificationIntervals.five_mins_before_start"),
  },
  {
    value: 15, // 15 minutes
    title: t("global.notificationIntervals.fifteen_mins_before_start"),
  },
  {
    value: 30, // 30 minutes
    title: t("ourhome.notifications.thirty_mins_before_start"),
  },
  {
    value: 60, // 1 hour
    title: t("global.notificationIntervals.one_hour_before_start"),
  },
  {
    value: 120, // 2 hours
    title: t("global.notificationIntervals.two_hours_before_start"),
  },
  {
    value: 1440, // 1 day
    title: t("global.notificationIntervals.one_day_before_start"),
  },
  {
    value: 2880, // 2 days
    title: t("global.notificationIntervals.two_days_before_start"),
  },
];

export const MODULE_THEMES: Record<Modules, ModuleTheme> = {
  news: {
    svg: "fas-messages-lines",
    color: "#A76800",
  },
  activities: {
    svg: "fas-person-running",
    color: "#613896",
  },
  employees: {
    svg: "fas-house-user",
    color: "#468202",
  },
  meetings: {
    svg: "fas-messages",
    color: "#D13884",
  },
  workSchedule: {
    svg: "fas-id-card",
    color: "#186571",
  },
  booking: {
    svg: "fas-key",
    color: "#6F7E00",
  },
  meals: {
    svg: "fas-utensils",
    color: "#0079D0",
  },
  media: {
    svg: "fas-photo-video",
    color: "#C24C1E",
  },
  groups: {
    svg: "fas-users",
    color: "#00434E",
  },
  planner: {
    svg: "fas-clipboard-check",
    color: "#813E3D",
  },
  admin: {
    svg: "fas-user-gear",
    color: "#00434E",
  },
};

export const DELETE_TYPE_MAP: Record<EditRepeatTypes, Delete_Type> = {
  single: Delete_Type.Single,
  repeating: Delete_Type.Series,
};

export const REPEAT_RULE_OPTIONS: IOption[] = [
  {
    value: null,
    title: "global.choose",
  },
  {
    value: 1,
    title: "repeats.options.daily",
  },
  {
    value: 7,
    title: "repeats.options.weekly",
  },
  {
    value: 30,
    title: "repeats.options.monthly",
  },
  {
    value: 365,
    title: "repeats.options.yearly",
  },
];

export const FILE_TYPE_TEXTS: Record<FileAcceptTypes, string> = {
  [FileAcceptTypes.OnlyPDF]: "PDF",
  [FileAcceptTypes.OnlyImage]: ".jpg, .png, .gif, .bmp, .tiff, .svg, .webp",
  [FileAcceptTypes.OnlyDocument]:
    ".doc, .docx, .pdf, .txt, .rtf, .odt, .xls, .xlsx, .ppt, .pptx",
  [FileAcceptTypes.ImageOrDocument]:
    ".jpg, .png, .gif, .bmp, .tiff, .svg, .webp, .doc, .docx, .pdf, .txt, .rtf, .odt, .xls, .xlsx, .ppt, .pptx",
};

export const MAX_FILE_SIZE_TEXTS: Record<FileSizes, string> = {
  [FileSizes.Max5MB]: "5 MB",
  [FileSizes.Max10MB]: "10 MB",
  [FileSizes.Max15MB]: "15 MB",
  [FileSizes.Max50MB]: "50 MB",
  [FileSizes.Max100MB]: "100 MB",
};

export const DAYS_IN_WEEK: Record<number, string> = {
  0: "global.monday",
  1: "global.tuesday",
  2: "global.wednesday",
  3: "global.thursday",
  4: "global.friday",
  5: "global.saturday",
  6: "global.sunday",
};

export const DEFAULT_RRULE_DAYS_STATE: RruleDaysState = {
  0: false,
  1: false,
  2: false,
  3: false,
  4: false,
  5: false,
  6: false,
};

export const DATE_PRESETS: Record<DatePresets, Intl.DateTimeFormatOptions> = {
  time: {
    hour: "2-digit",
    minute: "2-digit",
  },
  date: {
    day: "numeric",
    month: "long",
  },
  "date-time": {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  },
  "long-date": {
    weekday: "long",
    day: "numeric",
    month: "long",
  },
  "long-date-time": {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  },
};

export const YEAR_DISPLAYMENT_MAP: DatePresets[] = [
  "date",
  "date-time",
  "long-date",
  "long-date-time",
];

export const SCREEN_GRID_TEMPLATES = [
  {
    name: "2A",
    config: {
      gridSize: 4,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/3",
        },
      ],
    },
  },
  {
    name: "2B",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/3",
          colSpan: "1/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/4",
        },
      ],
    },
  },
  {
    name: "3A",
    config: {
      gridSize: 4,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/2",
        },
        {
          rowSpan: "2/3",
          colSpan: "2/3",
        },
      ],
    },
  },
  {
    name: "3B",
    config: {
      gridSize: 4,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/2",
        },
        {
          rowSpan: "1/2",
          colSpan: "2/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/3",
        },
      ],
    },
  },
  {
    name: "3C",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/4",
        },
      ],
    },
  },
  {
    name: "4A",
    config: {
      gridSize: 4,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/2",
        },
        {
          rowSpan: "1/2",
          colSpan: "2/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/2",
        },
        {
          rowSpan: "2/3",
          colSpan: "2/3",
        },
      ],
    },
  },
  {
    name: "4B",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/3",
          colSpan: "1/3",
        },
        {
          rowSpan: "1/2",
          colSpan: "3/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/4",
        },
      ],
    },
  },
  {
    name: "4C",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/3",
        },
        {
          rowSpan: "1/2",
          colSpan: "3/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/4",
        },
      ],
    },
  },
  {
    name: "4D",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/3",
        },
        {
          rowSpan: "3/4",
          colSpan: "3/4",
        },
      ],
    },
  },
  {
    name: "5A",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/3",
          colSpan: "1/3",
        },
        {
          rowSpan: "1/2",
          colSpan: "3/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/3",
        },
      ],
    },
  },
  {
    name: "5B",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/3",
        },
        {
          rowSpan: "1/2",
          colSpan: "3/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/4",
        },
      ],
    },
  },
  {
    name: "5C",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/3",
        },
        {
          rowSpan: "1/2",
          colSpan: "3/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/3",
        },
        {
          rowSpan: "3/4",
          colSpan: "3/4",
        },
      ],
    },
  },
  {
    name: "5D",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/3",
        },
        {
          rowSpan: "3/4",
          colSpan: "3/4",
        },
      ],
    },
  },
  {
    name: "6A",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/3",
        },
        {
          rowSpan: "1/2",
          colSpan: "3/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/3",
        },
        {
          rowSpan: "3/3",
          colSpan: "3/4",
        },
      ],
    },
  },
  {
    name: "6B",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/3",
          colSpan: "1/3",
        },
        {
          rowSpan: "1/2",
          colSpan: "3/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/2",
        },
        {
          rowSpan: "3/4",
          colSpan: "2/3",
        },
        {
          rowSpan: "3/4",
          colSpan: "3/4",
        },
      ],
    },
  },
  {
    name: "6C",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/3",
        },
        {
          rowSpan: "1/2",
          colSpan: "3/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/2",
        },
        {
          rowSpan: "3/4",
          colSpan: "2/3",
        },
        {
          rowSpan: "3/4",
          colSpan: "3/4",
        },
      ],
    },
  },
  {
    name: "6D",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/2",
        },
        {
          rowSpan: "2/3",
          colSpan: "2/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/3",
        },
        {
          rowSpan: "3/4",
          colSpan: "3/4",
        },
      ],
    },
  },
  {
    name: "7A",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/3",
        },
        {
          rowSpan: "1/2",
          colSpan: "3/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/2",
        },
        {
          rowSpan: "3/4",
          colSpan: "2/3",
        },
        {
          rowSpan: "3/4",
          colSpan: "3/4",
        },
      ],
    },
  },
  {
    name: "7B",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/3",
        },
        {
          rowSpan: "1/2",
          colSpan: "3/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/2",
        },
        {
          rowSpan: "2/3",
          colSpan: "2/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/3",
        },
        {
          rowSpan: "3/4",
          colSpan: "3/4",
        },
      ],
    },
  },
  {
    name: "7C",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/2",
        },
        {
          rowSpan: "2/3",
          colSpan: "2/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/2",
        },
        {
          rowSpan: "3/4",
          colSpan: "2/3",
        },
        {
          rowSpan: "3/4",
          colSpan: "3/4",
        },
      ],
    },
  },
  {
    name: "8A",
    config: {
      gridSize: 9,
      modules: [
        {
          rowSpan: "1/2",
          colSpan: "1/3",
        },
        {
          rowSpan: "1/2",
          colSpan: "3/4",
        },
        {
          rowSpan: "2/3",
          colSpan: "1/2",
        },
        {
          rowSpan: "2/3",
          colSpan: "2/3",
        },
        {
          rowSpan: "2/3",
          colSpan: "3/4",
        },
        {
          rowSpan: "3/4",
          colSpan: "1/2",
        },
        {
          rowSpan: "3/4",
          colSpan: "2/3",
        },
        {
          rowSpan: "3/4",
          colSpan: "3/4",
        },
      ],
    },
  },
];

export const HOME_SCREEN_MODULES = [
  {
    type: "ACTIVITIES",
    color: "#381466",
    id: 5,
  },
  {
    type: "BOOKING",
    color: "#ADC301",
    id: 3,
  },
  {
    type: "EXTERNAL_ACTIVITIES",
    color: "#591EA3",
    id: 10,
  },
  {
    type: "GALLERY",
    color: "#622A67",
    id: 6,
  },
  {
    type: "JOURNEY_PLANNER",
    color: "#363636",
    id: 11,
  },
  {
    type: "LATER_WORK_SCHEDULE",
    color: "#008C93",
    id: 99,
  },
  {
    type: "MEAL_PLAN",
    color: "#0080DC",
    id: 4,
  },
  {
    type: "MEETINGS",
    color: "#932C5F",
    id: 9,
  },
  {
    type: "NEWS",
    color: "#EA4400",
    id: 7,
  },
  {
    type: "OUR_HOME",
    color: "#58A202",
    id: 1,
  },
  {
    type: "SIMPLE_WORK_SCHEDULE",
    color: "#37A2A7",
    id: 12,
  },
  {
    type: "SLIDESHOW",
    color: "#622A67",
    id: 14,
  },
  {
    type: "WAYFINDER",
    color: "#37A2A7",
    id: 13,
  },
  {
    type: "WEATHER",
    color: "#363636",
    id: 8,
  },
  {
    type: "WORK_SCHEDULE",
    color: "#37A2A7",
    id: 2,
  },
];

export const FOOTER_MODULES = [
  {
    type: "ACTIVITIES",
    color: "#381466",
    id: 5,
  },
  {
    type: "BOOKING",
    color: "#ADC301",
    id: 3,
  },
  {
    type: "EXTERNAL_ACTIVITIES",
    color: "#591EA3",
    id: 10,
  },
  {
    type: "GALLERY",
    color: "#622A67",
    id: 6,
  },
  {
    type: "MEAL_PLAN",
    color: "#0080DC",
    id: 4,
  },
  {
    type: "MEETINGS",
    color: "#932C5F",
    id: 9,
  },
  {
    type: "NEWS",
    color: "#EA4400",
    id: 7,
  },
  {
    type: "OUR_HOME",
    color: "#58A202",
    id: 1,
  },
  {
    type: "SIMPLE_WORK_SCHEDULE",
    color: "#37A2A7",
    id: 12,
  },
  {
    type: "WORK_SCHEDULE",
    color: "#37A2A7",
    id: 2,
  },
];

export const APP_MODULES = [
  {
    type: "ACTIVITY",
    color: "#381466",
  },
  {
    type: "BOOKING",
    color: "#ADC301",
  },
  {
    type: "GALLERY",
    color: "#622A67",
  },
  {
    type: "GROUPS",
    color: "#000",
  },
  {
    type: "LIBRARY",
    color: "#004D84",
  },
  {
    type: "MEAL",
    color: "#0080DC",
  },
  {
    type: "MEETING",
    color: "#932C5F",
  },
  {
    type: "MY_ARCHIVE",
    color: "#000",
  },
  {
    type: "MY_DAY",
    color: "#000",
  },
  {
    type: "NEWS",
    color: "#EA4400",
  },
  {
    type: "PROFILES",
    color: "#468202",
  },
  {
    type: "WHATS_HAPPENING",
    color: "#000",
  },
  {
    type: "WORK_SCHEDULE",
    color: "#37A2A7",
  },
];
